import React from 'react';
import PropTypes from 'prop-types';
import { getUiLanguage } from '../../Utils/uiLocale.js';
import en from '../../locals/en.json';
import tr from '../../locals/tr.json';
import ar from '../../locals/ar.json';
import styles from './ErrorBoundary.module.css';

const CATALOGS = { en, tr, ar };

/**
 * The boundary renders outside LanguageProvider (see index.jsx / Utils/Provider.js),
 * so useLanguage() is unavailable here; read the catalogs for the last active UI language.
 */
function translate(key) {
  const read = (catalog) =>
    key.split('.').reduce((acc, part) => (acc == null ? undefined : acc[part]), catalog);
  return read(CATALOGS[getUiLanguage()]) ?? read(en) ?? key;
}

function ErrorBoundaryFallback({ error, errorInfo }) {
  return (
    <div className={styles.errorBoundary}>
      <h1 className={styles.errorTitle}>{translate('errorBoundary.title')}</h1>
      <div className={styles.errorMessage}>
        <h3 className={styles.errorLabel}>{translate('errorBoundary.errorDetails')}</h3>
        <pre className={styles.errorText} dir="ltr">
          {error && error.toString()}
        </pre>
        {error?.stack ? (
          <details className={styles.errorDetails} open>
            <summary className={styles.errorSummary}>
              {translate('errorBoundary.technicalDetails')}
            </summary>
            <pre className={styles.errorStack} dir="ltr">
              {error.stack}
            </pre>
          </details>
        ) : null}
      </div>
      {errorInfo && (
        <details className={styles.errorDetails}>
          <summary className={styles.errorSummary}>
            {translate('errorBoundary.componentStack')}
          </summary>
          <pre className={styles.errorStack} dir="ltr">
            {errorInfo.componentStack}
          </pre>
        </details>
      )}
      <button
        type="button"
        onClick={() => window.location.reload()}
        className={styles.reloadButton}
      >
        {translate('errorBoundary.reload')}
      </button>
    </div>
  );
}

ErrorBoundaryFallback.propTypes = {
  error: PropTypes.object,
  errorInfo: PropTypes.object,
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node,
};

export default ErrorBoundary;
